import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Skeleton, Alert, Chip,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import vouchersService from '../../services/vouchersService';
import { colors, designTokens } from '../../theme/theme';

export default function QRDisplayPage() {
  const { id } = useParams<{ id: string }>();
  const [qrData, setQrData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    vouchersService.getQR(id)
      .then(d => { setQrData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [id]);

  if (loading) return <Container maxWidth="xs" sx={{ py: 4 }}><Skeleton variant="rounded" height={400} /></Container>;
  if (error) return <Container maxWidth="xs" sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!qrData) return null;

  return (
    <Container maxWidth="xs" sx={{ py: 4 }}>
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: colors.primary.main }}>
          Your QR Code
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Show this to the barista at the venue
        </Typography>

        {/* QR interior stays WHITE intentionally — cameras need the
            contrast; only the outer frame is dark. */}
        <Box sx={{
          display: 'inline-flex', p: 1, borderRadius: 5,
          bgcolor: designTokens.glass.surface,
          border: `1px solid ${designTokens.glass.border}`,
          mb: 3,
        }}>
          <Box sx={{
            display: 'inline-flex', p: 3, borderRadius: 4,
            bgcolor: '#FFFFFF', // QR scanner contrast — DO NOT replace with glass
          }}>
            <QRCodeSVG
              value={qrData.qr_token}
              size={220}
              level="H"
              includeMargin={false}
              fgColor={colors.primary.dark}
            />
          </Box>
        </Box>

        {/* Short Code Fallback */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">Can't scan? Tell the barista this code:</Typography>
          <Typography variant="h4" sx={{
            fontWeight: 700, letterSpacing: '0.15em', mt: 0.5,
            fontFamily: "'JetBrains Mono', monospace", color: colors.primary.main,
          }}>
            {qrData.short_code}
          </Typography>
        </Box>

        {/* Status */}
        <Chip
          label={
            qrData.status === 'active' ? '✅ Ready to scan' :
            qrData.status === 'pending_match' ? '⏳ Waiting for match' :
            qrData.status
          }
          sx={{
            bgcolor: qrData.status === 'active' ? colors.success.subtle :
                     qrData.status === 'pending_match' ? colors.warning.subtle : colors.neutral[100],
            color: qrData.status === 'active' ? colors.success.dark :
                   qrData.status === 'pending_match' ? colors.warning.dark : colors.neutral[600],
            fontWeight: 700,
          }}
        />

        {/* Tier */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {qrData.tier === 'premium' ? '⭐ Premium Voucher' : 'Standard Voucher'}
        </Typography>
      </Paper>

      {/* Instructions */}
      <Box sx={{ mt: 3, px: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          📱 Present this QR to the barista when you arrive
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          ☕ They'll scan it and serve your drink
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ⏰ Find your CoffeeMates table and enjoy 90 minutes of connection!
        </Typography>
      </Box>
    </Container>
  );
}
